<script setup lang="ts">
import { computed } from "vue";
import { fromMaybe, isJust, matchMaybe, type Maybe } from "../lib/maybe";
import { dimOpacityForDistance } from "../editor/typewriterScroll";
import { sceneBreakMarkerText } from "../export/sceneFormatting";
import { peekParagraphDistance } from "../lib/sceneNeighborPeek";
import type { ZwParagraphStyle, ZwSceneFormatting } from "../lib/zwConfig";
import type { SceneNeighborPeekEdge } from "../composables/useSceneNeighborPeeks";
import {
  NO_EDITOR_CHAPTER_OPENING,
  type EditorChapterOpening,
} from "../editor/port";
import type { EntityPeekEntry } from "../editor/entityPeekIndex";
import type { ProsePeekContext } from "../editor/prose/proseEditor";
import ProseDocEditor from "../editor/prose/ProseDocEditor.vue";

const props = defineProps<{
  edge: "prev" | "next";
  peek: Maybe<SceneNeighborPeekEdge>;
  sceneFormatting: ZwSceneFormatting;
  /** Same Styling ▸ Paragraphs style as the open scene. */
  paragraphStyle: ZwParagraphStyle;
  resolveImageSrc: (src: string) => string;
  scrollHost: Maybe<HTMLElement>;
  entityIndex: readonly EntityPeekEntry[];
  mobile: boolean;
}>();

const emit = defineEmits<{
  open: [path: string];
}>();

const NO_PEEK: ProsePeekContext = {
  rootPath: () => null,
  manuscript: () => null,
  openEntry: () => {},
};

const chapterOpening: EditorChapterOpening = NO_EDITOR_CHAPTER_OPENING;

const peekText = computed(() =>
  matchMaybe(props.peek, {
    just: (value) => value.text,
    nothing: () => "",
  }),
);

const path = computed(() =>
  matchMaybe(props.peek, {
    just: (value) => value.path,
    nothing: () => "",
  }),
);

const blockCount = computed(() =>
  matchMaybe(props.peek, {
    just: (value) => value.blockCount,
    nothing: () => 0,
  }),
);

const breakMarker = computed(() => sceneBreakMarkerText(props.sceneFormatting));

const showBreak = computed(() => isJust(breakMarker.value));

const breakText = computed(() =>
  matchMaybe(breakMarker.value, {
    just: (text) => text,
    nothing: () => "",
  }),
);

const proseScrollHost = computed(() => fromMaybe(props.scrollHost));

/** CSS custom properties for per-block typewriter fade. */
const fadeStyle = computed(() => {
  const count = blockCount.value;
  const style: Record<string, string> = {
    "--zw-peek-blocks": String(count),
  };
  for (let index = 0; index < count; index += 1) {
    const distance = peekParagraphDistance(index, count, props.edge);
    style[`--zw-peek-op-${index + 1}`] = String(
      dimOpacityForDistance(distance),
    );
  }
  return style;
});

function onActivate(): void {
  if (path.value.length === 0) return;
  emit("open", path.value);
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  onActivate();
}
</script>

<template>
  <div
    v-if="isJust(peek)"
    class="scene-neighbor-peek"
    :data-edge="edge"
    :data-paragraph-style="paragraphStyle"
    data-first-paragraph="indented"
    data-opening-initial="none"
    data-opening-lead-in="none"
    :style="fadeStyle"
    role="button"
    tabindex="0"
    :aria-label="edge === 'prev' ? 'Open previous scene' : 'Open next scene'"
    @click="onActivate"
    @keydown="onKeydown"
  >
    <p
      v-if="edge === 'next' && showBreak"
      class="scene-neighbor-peek-break"
      aria-hidden="true"
    >
      {{ breakText }}
    </p>
    <ProseDocEditor
      class="scene-neighbor-peek-editor"
      :text="peekText"
      :editable="false"
      :resolve-image-src="resolveImageSrc"
      :scroll-host="proseScrollHost"
      :typewriter="false"
      :lint="false"
      :entity-index="entityIndex"
      :peek="NO_PEEK"
      :comments="false"
      :history-ranges="[]"
      :chapter-opening="chapterOpening"
      :mobile="mobile"
    />
    <p
      v-if="edge === 'prev' && showBreak"
      class="scene-neighbor-peek-break"
      aria-hidden="true"
    >
      {{ breakText }}
    </p>
  </div>
</template>

<style scoped>
/* Column metrics live on inner `.zw-prose` (same as the open scene). */
.scene-neighbor-peek {
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;
  border-radius: 0.2rem;
  transition: filter var(--duration-fast) var(--ease-out-quart);
}

/* Keep relative fade distances; only brighten the whole peek slightly. */
.scene-neighbor-peek:hover,
.scene-neighbor-peek:focus-visible {
  filter: brightness(1.12);
}

.scene-neighbor-peek:focus-visible {
  outline: 2px solid var(--accent, var(--text-strong));
  outline-offset: 2px;
}

.scene-neighbor-peek-editor {
  pointer-events: none;
}

/* Same typography column as the open scene; no extra editor chrome padding.
   Drop outward paragraph margins on the body-facing edge so only the open
   scene's padding sets the seam (keeps top/bottom gaps equal). */
.scene-neighbor-peek :deep(.zw-prose) {
  padding-top: 0;
  padding-bottom: 0;
  min-height: 0;
  caret-color: transparent;
}

.scene-neighbor-peek[data-edge="prev"] :deep(.zw-prose > *:last-child) {
  margin-bottom: 0;
}

.scene-neighbor-peek[data-edge="next"] :deep(.zw-prose > *:first-child) {
  margin-top: 0;
}

.scene-neighbor-peek[data-edge="prev"] .scene-neighbor-peek-break {
  margin-bottom: 0;
}

.scene-neighbor-peek[data-edge="next"] .scene-neighbor-peek-break {
  margin-top: 0;
}

.scene-neighbor-peek :deep(.zw-prose > *:nth-child(1)) {
  opacity: var(--zw-peek-op-1, 1);
}

.scene-neighbor-peek :deep(.zw-prose > *:nth-child(2)) {
  opacity: var(--zw-peek-op-2, 1);
}

.scene-neighbor-peek :deep(.zw-prose > *:nth-child(3)) {
  opacity: var(--zw-peek-op-3, 1);
}

.scene-neighbor-peek :deep(.zw-prose > *:nth-child(4)) {
  opacity: var(--zw-peek-op-4, 1);
}

.scene-neighbor-peek-break {
  box-sizing: border-box;
  width: 100%;
  max-width: 40rem;
  margin: 0.35em auto;
  margin-left: var(--zw-prose-margin-left, 0px);
  padding: 0 1.5rem;
  text-align: center;
  color: var(--text-muted);
  letter-spacing: 0.12em;
  font-family: var(--font-prose);
  font-size: calc(17px * var(--zw-ui-scale, 1));
  line-height: 1.7;
  opacity: 0.55;
}
</style>
